/**
 * UI Playground -- Admin Console
 *
 * Component showcase and design system reference page.
 * Ported from PaaS UiPlayground, adapted for ArgusAI-2 with TanStack Router.
 */
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Home, Building2, Users, Settings, Shield, Bell, BookOpen, ClipboardList,
  Plus, Pencil, Trash2, Save, Download, Upload, Search, Copy, RefreshCw,
  ChevronRight, ChevronDown, MoreHorizontal, Package,
  Eye as EyeIcon, EyeOff, Lock, Unlock, Clock, LogOut, User, Mail, CreditCard,
  CheckCircle2, AlertCircle, XCircle, Info, Loader2, Palette,
  Bot, Plug, Cpu, Zap, Workflow, ShieldCheck, BarChart3, Brain, Fingerprint,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';

// UI Components
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { DataTable } from '@/components/ui/data-table';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Card, CardHeader, CardTitle, CardContent, CardFooter, CardDescription,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
  SheetTrigger, SheetFooter,
} from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList,
  BreadcrumbPage, BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
// Playground helpers
import { PlaygroundSection } from '@/components/playground/PlaygroundSection';
import { ComponentPreviewCard } from '@/components/playground/ComponentPreviewCard';
import { IconTile } from '@/components/playground/IconTile';

// Brand icon
import { ArgusEye, type ArgusState } from '@/components/icons/ArgusEye';

export const Route = createFileRoute('/admin/playground')({
  beforeLoad: () => {
    throw redirect({
      to: '/ask-argus',
      replace: true,
    });
  },
  component: AdminPlaygroundPage,
});

// ─── Section definitions for sub-nav ────────────────────────────────────────

const UI_SECTIONS = [
  { id: 'argus-eye', title: 'ArgusEye' },
  { id: 'buttons', title: 'Buttons' },
  { id: 'badges', title: 'Badges' },
  { id: 'cards', title: 'Cards' },
  { id: 'forms', title: 'Forms & Inputs' },
  { id: 'tables', title: 'Tables' },
  { id: 'tabs', title: 'Tabs' },
  { id: 'overlays', title: 'Overlays' },
  { id: 'icons', title: 'Icons' },
  { id: 'feedback', title: 'Feedback' },
] as const;

// ─── Icon gallery data ──────────────────────────────────────────────────────

const ICON_CATEGORIES: Record<string, { icons: LucideIcon[]; description: string }> = {
  navigation: {
    icons: [Home, Building2, Users, BookOpen, ClipboardList, Bell, Settings, Shield, ChevronRight, ChevronDown],
    description: 'Page navigation and structural hierarchy icons.',
  },
  actions: {
    icons: [Plus, Pencil, Trash2, Save, Download, Upload, Search, Copy, RefreshCw, EyeIcon, EyeOff],
    description: 'User-triggered action icons for buttons and menus.',
  },
  status: {
    icons: [CheckCircle2, AlertCircle, XCircle, Info, Clock, Lock, Unlock, Loader2],
    description: 'State indicators and feedback icons.',
  },
  data: {
    icons: [BarChart3, Package, CreditCard, Mail, User, LogOut, MoreHorizontal],
    description: 'Data visualization and entity representation.',
  },
  'ai-governance': {
    icons: [Bot, Brain, Plug, Cpu, Zap, Workflow, ShieldCheck, Fingerprint, Palette],
    description: 'AI platform, agent, and governance-specific icons.',
  },
};

// ─── Sample table data ──────────────────────────────────────────────────────

const SAMPLE_TABLE_DATA = [
  { id: 'AGT-001', name: 'Maintenance Intelligence', status: 'active', type: 'Built-in', calls: 1284 },
  { id: 'AGT-002', name: 'Troubleshooting', status: 'active', type: 'Built-in', calls: 876 },
  { id: 'AGT-003', name: 'Custom Report Agent', status: 'inactive', type: 'Custom', calls: 42 },
  { id: 'AGT-004', name: 'Safety / LOTO', status: 'active', type: 'Built-in', calls: 531 },
  { id: 'AGT-005', name: 'Shift Handover', status: 'suspended', type: 'Built-in', calls: 299 },
];

// ─── Status badge helper ────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: 'bg-green-500/10 text-green-700 border-green-500/20',
    inactive: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
    suspended: 'bg-red-500/10 text-red-700 border-red-500/20',
    warning: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  };
  return (
    <Badge variant="outline" className={map[status] ?? ''}>
      {status}
    </Badge>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

function AdminPlaygroundPage() {
  const [eyeState, setEyeState] = useState<ArgusState>('idle');
  const [loadingBtn, setLoadingBtn] = useState(false);

  const simulateLoading = () => {
    setLoadingBtn(true);
    setTimeout(() => setLoadingBtn(false), 2000);
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto pb-20">
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="pb-6 border-b">
        <div className="flex items-center gap-3">
          <Palette className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight text-primary">UI Playground</h1>
            <p className="text-lg text-muted-foreground mt-1">ArgusAI Design System Blueprint &amp; Component Reference</p>
          </div>
        </div>
      </div>

      {/* ── Sticky sub-nav ────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 py-4 border-b -mx-6 px-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mr-4 hidden md:inline">
            Jump to:
          </span>
          {UI_SECTIONS.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'text-xs font-semibold text-muted-foreground hover:text-foreground h-8 px-3 rounded-full hover:bg-muted/80')}
            >
              {section.title}
            </a>
          ))}
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* A. ARGUS EYE STATES                                           */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <PlaygroundSection
        id="argus-eye"
        title="ArgusEye States"
        description="The primary animated brand icon for AI interactions. Click a state to preview."
      >
        <div className="max-w-md mx-auto">
          <ComponentPreviewCard label="ArgusEye (Organic AI)" className="flex-col items-center text-center p-6 bg-card/50">
            <div className="flex justify-center">
              <ArgusEye state={eyeState} className="w-[120px] h-[120px] mb-4" />
            </div>
            <div className="flex flex-col gap-4 w-full">
              <p className="text-[10px] font-mono uppercase tracking-tighter text-muted-foreground">
                State: <span className="text-foreground font-bold">{eyeState}</span>
              </p>
              <div className="flex gap-1 justify-center flex-wrap">
                {(['idle', 'thinking', 'speaking', 'alert', 'sleeping', 'listening'] as ArgusState[]).map((s) => (
                  <Button
                    key={s}
                    variant={eyeState === s ? (s === 'alert' ? 'destructive' : 'default') : 'outline'}
                    size="sm"
                    className="h-7 text-[10px]"
                    onClick={() => setEyeState(s)}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
          </ComponentPreviewCard>
        </div>
      </PlaygroundSection>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* B. BUTTONS                                                     */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <PlaygroundSection id="buttons" title="Buttons" description="All button variants, sizes, and states.">
        <div className="space-y-6">
          <ComponentPreviewCard label="Variants">
            <Button variant="default">Default</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="link">Link</Button>
          </ComponentPreviewCard>

          <ComponentPreviewCard label="With Icons">
            <Button><Plus className="mr-2 h-4 w-4" />Create</Button>
            <Button variant="outline"><Download className="mr-2 h-4 w-4" />Export</Button>
            <Button variant="secondary"><Search className="mr-2 h-4 w-4" />Search</Button>
            <Button variant="destructive"><Trash2 className="mr-2 h-4 w-4" />Delete</Button>
          </ComponentPreviewCard>

          <ComponentPreviewCard label="Loading State">
            <Button disabled={loadingBtn} onClick={simulateLoading}>
              {loadingBtn && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {loadingBtn ? 'Processing...' : 'Click to Load'}
            </Button>
          </ComponentPreviewCard>

          <ComponentPreviewCard label="Disabled">
            <Button disabled>Disabled Default</Button>
            <Button variant="outline" disabled>Disabled Outline</Button>
            <Button variant="secondary" disabled>Disabled Secondary</Button>
          </ComponentPreviewCard>

          <ComponentPreviewCard label="Sizes">
            <Button size="sm">Small</Button>
            <Button size="default">Default</Button>
            <Button size="lg">Large</Button>
            <Button size="icon" aria-label="Add (icon size example)" title="Icon size"><Plus className="h-4 w-4" /></Button>
          </ComponentPreviewCard>
        </div>
      </PlaygroundSection>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* C. BADGES                                                      */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <PlaygroundSection id="badges" title="Badges" description="Status indicators and labels.">
        <div className="space-y-6">
          <ComponentPreviewCard label="Standard Variants">
            <Badge variant="default">Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="destructive">Destructive</Badge>
            <Badge variant="outline">Outline</Badge>
          </ComponentPreviewCard>

          <ComponentPreviewCard label="Custom Status Colors">
            <StatusBadge status="active" />
            <StatusBadge status="inactive" />
            <StatusBadge status="suspended" />
            <StatusBadge status="warning" />
          </ComponentPreviewCard>

          <ComponentPreviewCard label="With Icons">
            <Badge variant="outline" className="gap-1">
              <CheckCircle2 className="h-3 w-3 text-green-600" />Healthy
            </Badge>
            <Badge variant="outline" className="gap-1">
              <AlertCircle className="h-3 w-3 text-amber-600" />Degraded
            </Badge>
            <Badge variant="outline" className="gap-1">
              <XCircle className="h-3 w-3 text-red-600" />Down
            </Badge>
            <Badge variant="outline" className="gap-1">
              <Loader2 className="h-3 w-3 animate-spin text-blue-600" />Loading
            </Badge>
          </ComponentPreviewCard>
        </div>
      </PlaygroundSection>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* D. CARDS                                                       */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <PlaygroundSection id="cards" title="Cards" description="Container patterns for content grouping.">
        <div className="space-y-6">
          <div className="grid md:grid-cols-3 gap-4">
            <ComponentPreviewCard label="Basic Card">
              <Card className="w-full">
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">A simple card with content only.</p>
                </CardContent>
              </Card>
            </ComponentPreviewCard>

            <ComponentPreviewCard label="Card with Header">
              <Card className="w-full">
                <CardHeader>
                  <CardTitle>Card Title</CardTitle>
                  <CardDescription>Supporting description text.</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">Card body content goes here.</p>
                </CardContent>
              </Card>
            </ComponentPreviewCard>

            <ComponentPreviewCard label="Card with Footer">
              <Card className="w-full">
                <CardHeader>
                  <CardTitle>Confirm Action</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">Are you sure you want to proceed?</p>
                </CardContent>
                <CardFooter className="flex justify-end gap-2">
                  <Button variant="outline" size="sm">Cancel</Button>
                  <Button size="sm">Confirm</Button>
                </CardFooter>
              </Card>
            </ComponentPreviewCard>
          </div>

          <ComponentPreviewCard label="Full-width Card with Table">
            <Card className="w-full">
              <CardHeader>
                <CardTitle>Resource List</CardTitle>
                <CardDescription>A common pattern for dashboard modules.</CardDescription>
              </CardHeader>
              <CardContent className="p-0 border-t">
                <Table>
                  <TableHeader className="bg-muted/30">
                    <TableRow>
                      <TableHead>Resource</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Production Database</TableCell>
                      <TableCell><Badge variant="outline">Healthy</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Primary Gateway</TableCell>
                      <TableCell><Badge variant="outline">Active</Badge></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </ComponentPreviewCard>
        </div>
      </PlaygroundSection>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* E. FORMS & INPUTS                                              */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <PlaygroundSection id="forms" title="Forms & Inputs" description="Form controls, labels, and input states.">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <ComponentPreviewCard label="Text Input">
              <div className="space-y-2 w-full">
                <Label>Standard Input</Label>
                <Input placeholder="Enter text..." />
              </div>
            </ComponentPreviewCard>

            <ComponentPreviewCard label="Textarea">
              <div className="space-y-2 w-full">
                <Label>Description</Label>
                <Textarea placeholder="Enter a longer description..." rows={3} />
              </div>
            </ComponentPreviewCard>

            <ComponentPreviewCard label="Select Dropdown">
              <div className="space-y-2 w-full">
                <Label>Model Selection</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select model..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="qwen-72b">Qwen 2.5 72B AWQ</SelectItem>
                    <SelectItem value="qwen-32b">Qwen 2.5 32B</SelectItem>
                    <SelectItem value="llama-3b">Llama 3.2 3B</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </ComponentPreviewCard>
          </div>

          <div className="space-y-4">
            <ComponentPreviewCard label="Checkbox">
              <div className="space-y-3 w-full">
                <div className="flex items-center space-x-2">
                  <Checkbox id="cb1" defaultChecked />
                  <Label htmlFor="cb1">Enable guardrails</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="cb2" />
                  <Label htmlFor="cb2">Enable PII detection</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="cb3" disabled />
                  <Label htmlFor="cb3" className="text-muted-foreground">Disabled option</Label>
                </div>
              </div>
            </ComponentPreviewCard>

            <ComponentPreviewCard label="Switch">
              <div className="space-y-3 w-full">
                <div className="flex items-center justify-between">
                  <Label htmlFor="sw1">Feature toggle</Label>
                  <Switch id="sw1" defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="sw2">Maintenance mode</Label>
                  <Switch id="sw2" />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="sw3" className="text-muted-foreground">Disabled</Label>
                  <Switch id="sw3" disabled />
                </div>
              </div>
            </ComponentPreviewCard>

            <ComponentPreviewCard label="Radio Group">
              <RadioGroup defaultValue="standard" className="w-full space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="basic" id="rg1" />
                  <Label htmlFor="rg1">Basic profile</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="standard" id="rg2" />
                  <Label htmlFor="rg2">Standard profile</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="maximum" id="rg3" />
                  <Label htmlFor="rg3">Maximum profile</Label>
                </div>
              </RadioGroup>
            </ComponentPreviewCard>

            <ComponentPreviewCard label="Disabled Input">
              <div className="space-y-2 w-full">
                <Label className="text-muted-foreground">Locked Field</Label>
                <Input value="Cannot be changed" disabled />
              </div>
            </ComponentPreviewCard>
          </div>
        </div>
      </PlaygroundSection>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* F. TABLES                                                      */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <PlaygroundSection id="tables" title="Tables" description="Standard DataTable component — sortable columns, pagination, row actions. Use this for ALL tables.">
        <ComponentPreviewCard label="DataTable — Standard (Sortable + Paginated)">
          <div className="w-full">
            <DataTable
              columns={[
                { key: 'id', label: 'ID', width: 'w-[80px]', render: (r) => <span className="font-mono text-xs">{r.id}</span> },
                { key: 'name', label: 'Name', render: (r) => <span className="font-medium">{r.name}</span> },
                { key: 'type', label: 'Type', render: (r) => <Badge variant={r.type === 'Built-in' ? 'secondary' : 'outline'}>{r.type}</Badge> },
                { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
                { key: 'calls', label: 'Calls', align: 'right', render: (r) => <span className="font-mono">{r.calls.toLocaleString()}</span> },
                {
                  key: 'actions', label: 'Actions', align: 'right', sortable: false, stopPropagation: true,
                  render: () => (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions" title="Actions"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem><EyeIcon className="mr-2 h-4 w-4" />View</DropdownMenuItem>
                        <DropdownMenuItem><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive focus:text-destructive"><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ),
                },
              ]}
              data={SAMPLE_TABLE_DATA}
              pageSize={5}
              defaultSortKey="name"
              onRowClick={(row) => toast.info(`Clicked: ${row.name}`)}
              emptyMessage="No agents registered"
            />
          </div>
        </ComponentPreviewCard>
        <div className="mt-2 p-3 bg-muted/30 rounded-lg text-xs text-muted-foreground">
          <p className="font-bold text-foreground mb-1">Usage: import {'{ DataTable }'} from '@/components/ui/data-table'</p>
          <p>• All columns are sortable by default (set sortable: false to disable)</p>
          <p>• Pagination appears automatically when data exceeds pageSize</p>
          <p>• Use render() for custom cell content (badges, actions, etc.)</p>
          <p>• Set stopPropagation: true on action columns to prevent row click</p>
        </div>
      </PlaygroundSection>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* G. TABS                                                        */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <PlaygroundSection id="tabs" title="Tabs" description="Navigation tabs and segmented controls.">
        <div className="space-y-6">
          <ComponentPreviewCard label="Standard Horizontal Tabs">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="config">Configuration</TabsTrigger>
                <TabsTrigger value="logs">Logs</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="mt-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Overview tab content.</p>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="config" className="mt-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Configuration tab content.</p>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="logs" className="mt-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Logs tab content.</p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </ComponentPreviewCard>

          <ComponentPreviewCard label="Tabs with Badge Counts">
            <Tabs defaultValue="active" className="w-full">
              <TabsList>
                <TabsTrigger value="active" className="gap-1.5">
                  Active <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">12</Badge>
                </TabsTrigger>
                <TabsTrigger value="paused" className="gap-1.5">
                  Paused <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">3</Badge>
                </TabsTrigger>
                <TabsTrigger value="archived" className="gap-1.5">
                  Archived <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">48</Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </ComponentPreviewCard>

          <ComponentPreviewCard label="Branded Segmented Choice">
            <Tabs defaultValue="standard" className="w-[500px]">
              <TabsList className="grid w-full grid-cols-3 h-auto p-1 bg-sidebar border border-sidebar-border shadow-inner rounded-xl">
                {['Basic', 'Standard', 'Maximum'].map((label) => (
                  <TabsTrigger
                    key={label.toLowerCase()}
                    value={label.toLowerCase()}
                    className="data-[state=active]:bg-sidebar-primary data-[state=active]:text-sidebar-primary-foreground data-[state=active]:shadow-md hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-lg transition-all duration-200"
                  >
                    <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </ComponentPreviewCard>
        </div>
      </PlaygroundSection>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* OVERLAYS                                                       */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <PlaygroundSection id="overlays" title="Overlays & Pop-ups" description="Dialogs, sheets, dropdown menus, and breadcrumbs.">
        <div className="grid md:grid-cols-2 gap-6">
          <ComponentPreviewCard label="Dropdown Menu">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">Open Dropdown <ChevronDown className="ml-2 h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem><User className="mr-2 h-4 w-4" /> Profile</DropdownMenuItem>
                <DropdownMenuItem><CreditCard className="mr-2 h-4 w-4" /> Billing</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </ComponentPreviewCard>

          <ComponentPreviewCard label="Dialog">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">Open Dialog</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Edit Agent</DialogTitle>
                  <DialogDescription>Update the agent configuration. Click save when done.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="agent-name" className="text-right">Name</Label>
                    <Input id="agent-name" defaultValue="Maintenance Intelligence" className="col-span-3" />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit">Save changes</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </ComponentPreviewCard>

          <ComponentPreviewCard label="Side Panel (Sheet)">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline">Open Side Panel</Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Agent Details</SheetTitle>
                  <SheetDescription>Detailed attributes for the selected agent.</SheetDescription>
                </SheetHeader>
                <div className="py-6 space-y-4">
                  <div className="space-y-1">
                    <Label className="text-xs uppercase font-bold text-muted-foreground">Agent ID</Label>
                    <p className="font-mono text-sm">AGT-001</p>
                  </div>
                  <Separator />
                  <div className="space-y-1">
                    <Label className="text-xs uppercase font-bold text-muted-foreground">Owner</Label>
                    <p className="text-sm">Platform Admin</p>
                  </div>
                </div>
                <SheetFooter><Button className="w-full">Save Changes</Button></SheetFooter>
              </SheetContent>
            </Sheet>
          </ComponentPreviewCard>

          <ComponentPreviewCard label="Breadcrumbs">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem><BreadcrumbLink href="#">Admin</BreadcrumbLink></BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem><BreadcrumbLink href="#">Governance</BreadcrumbLink></BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem><BreadcrumbPage>Guardrails</BreadcrumbPage></BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </ComponentPreviewCard>
        </div>
      </PlaygroundSection>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* H. ICONS GALLERY                                               */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <PlaygroundSection id="icons" title="Icon Gallery" description="Standard icon set powered by lucide-react. Hover for details, click the copy button to copy JSX.">
        <div className="space-y-10">
          {Object.entries(ICON_CATEGORIES).map(([category, { icons, description }]) => (
            <div key={category} className="space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground border-l-4 border-primary pl-2 capitalize">
                {category.replace('-', ' / ')}
              </h3>
              <p className="text-xs text-muted-foreground">{description}</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                {icons.map((Icon) => (
                  <IconTile key={Icon.displayName || 'Icon'} icon={Icon} name={Icon.displayName || 'Icon'} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </PlaygroundSection>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* I. ALERTS & FEEDBACK                                           */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <PlaygroundSection id="feedback" title="Alerts & Feedback" description="Status indicators, loading patterns, and notifications.">
        <div className="space-y-6">
          <div className="grid gap-4">
            <ComponentPreviewCard label="Alert: Informational">
              <Alert className="w-full">
                <Info className="h-4 w-4" />
                <AlertTitle>Heads up!</AlertTitle>
                <AlertDescription>This is a standard informational alert for non-critical notices.</AlertDescription>
              </Alert>
            </ComponentPreviewCard>

            <ComponentPreviewCard label="Alert: Destructive">
              <Alert variant="destructive" className="w-full">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>Something went wrong. The guardrail engine returned an unexpected response.</AlertDescription>
              </Alert>
            </ComponentPreviewCard>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <ComponentPreviewCard label="Progress Bar">
              <div className="space-y-3 w-full">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase text-muted-foreground">Document Ingestion</Label>
                  <Progress value={72} />
                  <p className="text-xs text-muted-foreground text-right">72%</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase text-muted-foreground">VRAM Usage</Label>
                  <Progress value={38} />
                  <p className="text-xs text-muted-foreground text-right">38%</p>
                </div>
              </div>
            </ComponentPreviewCard>

            <ComponentPreviewCard label="Skeleton Loader">
              <div className="flex items-center space-x-4 w-full">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            </ComponentPreviewCard>
          </div>

          <ComponentPreviewCard label="Toast Notifications">
            <Button
              variant="outline"
              onClick={() => toast.success('Agent deployed successfully.')}
            >
              Success Toast
            </Button>
            <Button
              variant="outline"
              onClick={() => toast.error('Failed to connect to vLLM server.')}
            >
              Error Toast
            </Button>
            <Button
              variant="outline"
              onClick={() => toast.info('Document re-indexing in progress...')}
            >
              Info Toast
            </Button>
            <Button
              variant="outline"
              onClick={() => toast.warning('GPU VRAM usage exceeded 90%.')}
            >
              Warning Toast
            </Button>
          </ComponentPreviewCard>
        </div>
      </PlaygroundSection>
    </div>
  );
}
